// Servicio de almacenamiento de archivos multimedia usando IndexedDB
export interface StoredMediaItem {
  id: string;
  type: 'image' | 'video';
  url: string; // Blob URL para archivos locales o URL externa
  duration?: number;
  name?: string;
  size?: number;
  uploaded?: boolean;
  fileData?: ArrayBuffer; // Los datos binarios del archivo para archivos locales
  mimeType?: string;
  lastModified?: number;
}

class MediaStorageService {
  private dbName = 'NeurolimpiadasMediaDB';
  private dbVersion = 1;
  private storeName = 'mediaFiles';
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<void> | null = null;
  // Trackea las Blob URLs vivas para poder revocarlas al eliminar o refrescar.
  private blobUrls = new Map<string, string>();

  constructor() {
    if (typeof window === 'undefined' || typeof indexedDB === 'undefined') return;
    // Iniciamos lazy en ensureDB; evita explotar si el módulo se carga en un entorno sin IndexedDB.
  }

  private initDB(): Promise<void> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      request.onerror = () => {
        console.error('Error al abrir IndexedDB:', request.error);
        this.dbPromise = null;
        reject(request.error);
      };
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('uploaded', 'uploaded', { unique: false });
        }
      };
    });
    return this.dbPromise;
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) await this.initDB();
    if (!this.db) throw new Error('IndexedDB no disponible');
    return this.db;
  }

  private trackBlobUrl(id: string, url: string): void {
    const previous = this.blobUrls.get(id);
    if (previous && previous !== url && previous.startsWith('blob:')) {
      try { URL.revokeObjectURL(previous); } catch { /* noop */ }
    }
    if (url.startsWith('blob:')) this.blobUrls.set(id, url);
  }

  // Guardar archivo multimedia
  async saveMediaFile(file: File): Promise<StoredMediaItem> {
    try {
      const db = await this.ensureDB();
      const arrayBuffer = await file.arrayBuffer();
      const url = URL.createObjectURL(file);
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      this.trackBlobUrl(id, url);

      const mediaItem: StoredMediaItem = {
        id,
        type: file.type.startsWith('video/') ? 'video' : 'image',
        url,
        duration: file.type.startsWith('video/') ? undefined : 5000,
        name: file.name,
        size: file.size,
        uploaded: true,
        fileData: arrayBuffer,
        mimeType: file.type,
        lastModified: file.lastModified
      };

      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      await new Promise<void>((resolve, reject) => {
        const request = store.add(mediaItem);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      console.log('💾 Archivo guardado en IndexedDB:', mediaItem.name);
      return mediaItem;
    } catch (error) {
      console.error('Error al guardar archivo en IndexedDB:', error);
      throw error;
    }
  }

  // Guardar URL externa
  async saveMediaURL(url: string, type: 'image' | 'video', name?: string, duration?: number): Promise<StoredMediaItem> {
    try {
      const db = await this.ensureDB();
      
      const mediaItem: StoredMediaItem = {
        id: `url-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: type,
        url: url,
        duration: duration || (type === 'image' ? 5000 : undefined),
        name: name || `${type}-${Date.now()}`,
        uploaded: false
      };

      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      await new Promise<void>((resolve, reject) => {
        const request = store.add(mediaItem);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      console.log('💾 URL guardada en IndexedDB:', url);
      return mediaItem;
    } catch (error) {
      console.error('Error al guardar URL en IndexedDB:', error);
      throw error;
    }
  }

  // Recuperar todos los archivos multimedia
  async getAllMedia(): Promise<StoredMediaItem[]> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => {
          const items = request.result as StoredMediaItem[];
          console.log(`📁 ${items.length} archivos recuperados de IndexedDB`);
          resolve(items);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error al recuperar archivos de IndexedDB:', error);
      return [];
    }
  }

  // Restaurar URLs de Blob para archivos locales
  async restoreMediaURLs(): Promise<StoredMediaItem[]> {
    try {
      const allMedia = await this.getAllMedia();
      const restoredMedia: StoredMediaItem[] = [];

      for (const item of allMedia) {
        if (item.uploaded && item.fileData && item.mimeType) {
          const blob = new Blob([item.fileData], { type: item.mimeType });
          const newUrl = URL.createObjectURL(blob);
          // Revocamos la Blob URL previa de este item si existía.
          this.trackBlobUrl(item.id, newUrl);
          restoredMedia.push({ ...item, url: newUrl });
        } else {
          restoredMedia.push(item);
        }
      }

      console.log(`🔄 ${restoredMedia.length} archivos restaurados con URLs válidas`);
      return restoredMedia;
    } catch (error) {
      console.error('Error al restaurar URLs de archivos:', error);
      return [];
    }
  }

  // Eliminar archivo
  async deleteMedia(id: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      await new Promise<void>((resolve, reject) => {
        const request = store.delete(id);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      // Revocamos la Blob URL asociada para liberar memoria.
      const url = this.blobUrls.get(id);
      if (url && url.startsWith('blob:')) {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        this.blobUrls.delete(id);
      }
    } catch (error) {
      console.error('Error al eliminar archivo de IndexedDB:', error);
      throw error;
    }
  }

  // Limpiar todos los archivos
  async clearAllMedia(): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      // Revocamos todas las Blob URLs trackeadas.
      this.blobUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          try { URL.revokeObjectURL(url); } catch { /* noop */ }
        }
      });
      this.blobUrls.clear();
    } catch (error) {
      console.error('Error al limpiar IndexedDB:', error);
      throw error;
    }
  }

  // Obtener estadísticas
  async getStorageStats(): Promise<{ total: number; images: number; videos: number; totalSize: number }> {
    try {
      const allMedia = await this.getAllMedia();
      const stats = {
        total: allMedia.length,
        images: allMedia.filter(m => m.type === 'image').length,
        videos: allMedia.filter(m => m.type === 'video').length,
        totalSize: allMedia.reduce((sum, m) => sum + (m.size || 0), 0)
      };
      
      return stats;
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
      return { total: 0, images: 0, videos: 0, totalSize: 0 };
    }
  }
}

// Instancia singleton
export const mediaStorageService = new MediaStorageService();
